import React, { useEffect, useRef, useLayoutEffect, useState } from "react";
import {
  useParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  PatternlibOverlayContainer,
  PatternlibButton,
  PatternlibSpinner,
  PatternlibToast,
  PatternlibAccordionItem,
  PatternlibAccordion,
} from "@liebherr/patternlib-react";
import { useTranslation } from "react-i18next";
import styles from "./Configurator.module.css";
import Viewer from "../../components/viewer/Viewer.js";
import Dropdown from "../../components/dropdown/Dropdown.js";
import Slider from "../../components/slider/Slider";
import InfoButton from "../../components/info-button/InfoButton";
import Nav from "../../components/nav/Nav";
import AddComparisonButton from "../../components/add-comparison-button/AddComparisonButton";
import ImageSelector from "../../components/image-selector/ImageSelector";
import {
  copyToClipboard,
  getTableData,
  getMidentData,
  getTableDataOnChange,
  getLanguage,
  validateInfoKeys,
  checker,
  pushToDataLayer,
} from "../../helpers/helpers";
import { apiURL, apiKey, catalog } from "../../config/api";
import KeyButton from "../../components/key-button/KeyButton";
import Input from "../../components/input/Input";
import useMutationObserver from "../../hooks/useMutationObserver";

function Configurator({ isDemoUser }) {
  const { t, i18n } = useTranslation();

  const [data, setData] = useState();
  const [value, setValue] = useState({});
  const [part, setPart] = useState({});
  const [partkey, setPartkey] = useState();
  const [loading, setLoading] = useState();
  const [fixed, setFixed] = useState([false, false]);
  const [fixedKey, setFixedKey] = useState();
  const [searchParams] = useSearchParams();
  const overlayRef = useRef();

  const navigate = useNavigate();

  // use mutation observer to get current screen size
  const options = {
    config: { attributes: true, childList: true, subtree: true },
  };
  const target = document.documentElement;
  const attributeName = "breakpoint";
  const screen = useMutationObserver(options, target, attributeName);

  // the useRef Hook allows to persist data between renders
  const varsetRef = useRef();
  const toastRef = useRef();

  // get params
  const { projectPath } = useParams();

  /**
   * Checks if the given URL is valid and navigates to the homepage if not
   */
  const doNothing = () => {
    return null;
  };

  projectPath !== "series380" &&
  projectPath !== "series260" &&
  projectPath !== "iso6022"
    ? navigate("/")
    : doNothing();

  let location = useLocation();

  const path = "liebherr_components/" + projectPath + "_asmtab.prj";

  // create an object which will serve as the order template
  const objectOrder = { D: null, SD: null };

  const configDetailsGA = (productKey) => {
    const eventName = "configuration_details";
    const pagePath = location.pathname;
    const pageType = "configurator";
    const language =
      i18n.language === "de-DE" || i18n.language === "de" ? "de-DE" : "en-EN";
    const product_key = productKey;
    let props = { pagePath, pageType, language, product_key };

    pushToDataLayer(eventName, props);
  };

  const getTable = (mident, lines) => {
    getTableData(
      apiURL,
      path,
      apiKey,
      getLanguage(i18n.language),
      mident,
      lines
    ).then((data) => {
      if (data !== undefined) {
        if (sessionStorage.getItem("data")) {
          // if sessionStorage has data from last saving: take these data and not the default data
          data = JSON.parse(sessionStorage.getItem("data"));
        }
        const varset = data.index.varsettransfer;
        const partkey = data.index["LINA"];
        const path = data.index.path;
        varsetRef.current = varset;

        let values = data.index.line.values;
        values = Object.assign(objectOrder, values);

        setPart({ path, varset });
        setData(values);
        setPartkey(partkey);
      }
    });
  };

  const getTableChange = async (variable, value, varset, valueD, valueSD) => {
    // set if the key D or SD should be fixed (show only filtered and separate ordered data for the key)
    let arr = fixed;
    if (variable === "D" || variable === "SD") {
      const key = variable;

      if (!checker(arr)) {
        // set the inital selection (need for logic to show unfiltered data in dropdown)
        setFixedKey(key);
      }

      arr.splice(key === "D" ? 0 : 1, 1, true);
      setFixed(arr);
    }

    getTableDataOnChange(
      apiURL,
      path,
      apiKey,
      getLanguage(i18n.language),
      variable,
      value,
      varset,
      valueD,
      valueSD
    ).then((data) => {
      if (data !== undefined) {
        sessionStorage.setItem("data", JSON.stringify(data.tableData)); // write current setting to the session storage
        const varset = data.tableData.index.varsettransfer;
        const partkey = data.tableData.index["LINA"];
        const path = data.tableData.index.path;
        varsetRef.current = varset;

        let values = data.tableData.index.line.values;
        values = Object.assign(objectOrder, values);

        // get the values for the keys D and SD from response
        const fixedD = data.dataForValueD.index.line.values["D"];
        const fixedSD = data.dataForValueSD.index.line.values["SD"];
        const obj = { ...values, fixedD, fixedSD };

        setPart({ path, varset });
        setData(obj);
        setPartkey(partkey);
      }
    });
  };

  const updateValues = () => (e) => {
    const variable = e.variable;
    const value = e.value;
    setValue({ variable, value });
  };

  const createToast = () => () => {
    const message = t("AddComparisonButton.savedToast");
    const toast = toastRef.current;
    toast.createToastItem({
      itemId: "200",
      message: message,
      type: "success",
      autoHide: true,
    });
  };

  const toSummary = () => {
    configDetailsGA(partkey);
    navigate("/summary/" + projectPath, {
      state: { key: partkey },
    });
  };

  const copyKeyToClipboard = () => async () => {
    const toastItem = await copyToClipboard(partkey, t("KeyButton.message"));
    if (toastItem !== null) {
      const toast = toastRef.current;
      toast.createToastItem({
        ...toastItem,
      });
    }
  };

  const getMident = (part) => {
    getMidentData(apiURL, part, apiKey, catalog).then((data) => {
      const mident = data["mident"] ? data["mident"] : null;
      if (mident === null) {
        // alert message
        const message = t("configurator.partKeyError");
        const toast = toastRef.current;

        toast.createToastItem({
          itemId: "2",
          message: message,
          type: "alert",
          autoHide: false,
        });
      }
      // load configuration from mident
      getTable(mident);
    });
  };

  const resetFixedValues = (key) => () => {
    setFixedKey(undefined);
    setFixed([false, false]);
  };

  useEffect(() => {
    if (Object.keys(value).length >= 1) {
      // get current value for the keys D and SD and pass to function
      const valueD =
        value["variable"] === "D" ? value["value"] : data["D"]["resolvedVal"];

      const valueSD =
        value["variable"] === "SD" ? value["value"] : data["SD"]["resolvedVal"];

      getTableChange(
        value["variable"],
        value["value"],
        varsetRef.current,
        valueD,
        valueSD
      );
    }
  }, [value]);

  useLayoutEffect(() => {
    if (location.state !== null) {
      if (location.state.key !== undefined) {
        // function to get mident from key and then fetch table with mident
        const key = location.state.key;
        getMident(key);
      } else if (location.state.lines !== undefined) {
        // load from varset
        getTable(null, location.state.lines);
      } else {
        // load default configuration
        getTable();

        // alert message
        const message = t("configurator.partKeyError");
        const toast = toastRef.current;

        toast.createToastItem({
          itemId: "2",
          message: message,
          type: "alert",
          autoHide: false,
        });
      }
    } else {
      // check if user reached site with query param for BAUMA:
      if (searchParams.get("productkey")) {
        getMident(searchParams.get("productkey"));
      } else {
        // load default configuration
        getTable();
      }
    }
  }, [projectPath, location]);

  const uiLoader = () =>
    Object.keys(data).map(function (key) {
      const obj = data[key];
      const unit = obj["Unit"] !== "" ? ` (${obj["Unit"]})` : "";
      const excludeKeys = ["POS", "SAEINFO", "P", "fixedD", "fixedSD"];
      const sliderKeys = ["EL", "HUB"];
      // Exclude keys
      if (!excludeKeys.includes(key)) {
        if (
          obj["status"] === "static" ||
          (obj["status"] === "" && !sliderKeys.includes(key)) ||
          obj["status"] === "finished" ||
          (obj["status"] === "fixed" && key === "MT") ||
          (obj["status"] === "fixed" && key === "FN1") ||
          (obj["status"] === "fixed" && key === "FN2") ||
          (obj["status"] === "fixed" && key === "D") ||
          (obj["status"] === "fixed" && key === "SD")
        ) {
          if (obj["isImageVariable"] === true) {
            return (
              <div
                key={key + "WRAPPER"}
                className={
                  validateInfoKeys(projectPath, key)
                    ? `${styles.inputWrapper} ${styles.start}`
                    : styles.inputWrapperFullWidth
                }
              >
                <ImageSelector
                  data={data[key]}
                  variable={key}
                  project={projectPath}
                  onChange={updateValues()}
                  disabled={obj["status"] === "finished"}
                ></ImageSelector>
                <InfoButton
                  title={obj["desc"]}
                  abbreviation={key}
                  path={projectPath}
                  cadenasInfo={data[key + "INFO"]}
                  key={key + "INFO"}
                ></InfoButton>
              </div>
            );
          } else {
            return (
              <div
                key={key + "WRAPPER"}
                className={
                  validateInfoKeys(projectPath, key)
                    ? `${styles.inputWrapper}`
                    : styles.inputWrapperFullWidth
                }
              >
                <Dropdown
                  label={obj["desc"] + unit}
                  placeholder={
                    key === "FN1" ||
                    key === "FN2" ||
                    key === "D" ||
                    key === "SD"
                      ? obj["resolvedVal"]
                      : ""
                  }
                  data={data}
                  variable={key}
                  onChange={updateValues()}
                  key={key}
                  disabled={
                    obj["status"] === "finished" ||
                    (obj["status"] === "fixed" && key === "MT") ||
                    (obj["status"] === "fixed" && key === "FN1") ||
                    (obj["status"] === "fixed" && key === "FN2") ||
                    (obj["status"] === "fixed" && key === "D") ||
                    (obj["status"] === "fixed" && key === "SD")
                  }
                  fixed={{ fixed, fixedKey }}
                  onReset={resetFixedValues()}
                  closeOnScroll={
                    screen === "isMobile" || screen === "isTablet"
                      ? false
                      : true
                  }
                ></Dropdown>
                <div
                  style={{
                    paddingTop: validateInfoKeys(projectPath, key)
                      ? "28px"
                      : "0px",
                  }}
                >
                  <InfoButton
                    title={obj["desc"]}
                    abbreviation={key}
                    path={projectPath}
                    cadenasInfo={
                      key === "SAEPOS" ? data["SAEINFO"] : data[key + "INFO"]
                    }
                    key={key + "INFO"}
                    showInfoText={key === "SAEPOS" ? false : true}
                  ></InfoButton>
                </div>
              </div>
            );
          }
        } else if (
          obj["status"] === "ranged" ||
          (obj["status"] === "" && sliderKeys.includes(key))
        ) {
          return (
            <div
              key={key + "WRAPPER"}
              className={
                validateInfoKeys(projectPath, key)
                  ? `${styles.inputWrapper} ${styles.center}`
                  : styles.inputWrapperFullWidth
              }
            >
              <Slider
                label={obj["desc"] + unit}
                data={data[key]}
                variable={key}
                onChange={updateValues()}
                key={key}
              ></Slider>
              <InfoButton
                title={obj["desc"]}
                abbreviation={key}
                path={projectPath}
                key={key + "INFO"}
              ></InfoButton>
              <div className={styles.sliderInputWrapper}>
                <Input
                  width="128px"
                  step="10"
                  data={data[key]}
                  variable={key}
                  onChange={updateValues()}
                ></Input>
              </div>
            </div>
          );
        }
      }
    });

  useEffect(() => {
    const onProgressBegin = () => {
      setLoading(true);
    };
    window.addEventListener("hyzProgressBegin", onProgressBegin);
    return () => {
      window.removeEventListener("hyzProgressBegin", onProgressBegin);
    };
  }, []);

  useEffect(() => {
    const onProgressEnd = () => {
      setLoading(false);
    };
    window.addEventListener("hyzProgressEnd", onProgressEnd);
    return () => {
      window.removeEventListener("hyzProgressEnd", onProgressEnd);
    };
  }, []);

  useEffect(() => {
    if (loading) {
      // Select content class and add style
      const ref = overlayRef.current;
      const element = ref.shadowRoot.querySelector(".content");
      element.style.zIndex = "9999";
    }
  }, [loading]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <Nav
            partkey={partkey}
            projectPath={projectPath}
            activeTab="1"
            isDemoUser={isDemoUser}
          ></Nav>
        </div>
        <div className={styles.body}>
          <div className={styles.sidebar}>
            {partkey && (
              <div className={styles.keyContainer}>
                <div className={styles.keyWrapper}>
                  {screen === "isMobile" || screen === "isTablet" ? (
                    <PatternlibAccordion type="faq">
                      <PatternlibAccordionItem
                        label={t("infotexts.general.key.title")}
                      >
                        <KeyButton
                          partkey={partkey}
                          onPress={copyKeyToClipboard()}
                          title={t("infotexts.general.key.title")}
                          buttonLabel={t("KeyButton.copy")}
                        ></KeyButton>
                      </PatternlibAccordionItem>
                    </PatternlibAccordion>
                  ) : (
                    <KeyButton
                      partkey={partkey}
                      onPress={copyKeyToClipboard()}
                      title={t("infotexts.general.key.title")}
                      buttonLabel={t("KeyButton.copy")}
                    ></KeyButton>
                  )}
                </div>
              </div>
            )}
            <Viewer part={part}></Viewer>
            {data && (
              <div className={styles.posContainer}>
                <div className={styles.posWrapper}>
                  <div
                    key={"POS" + "WRAPPER"}
                    className={
                      validateInfoKeys(projectPath, "POS")
                        ? `${styles.inputWrapper} ${styles.center} ${styles.background}`
                        : styles.inputWrapperFullWidth
                    }
                  >
                    <Slider
                      label={data["POS"]["desc"]}
                      data={data["POS"]}
                      variable={"POS"}
                      onChange={updateValues()}
                      key={"POS"}
                    ></Slider>
                    <InfoButton
                      title={data["POS"]["desc"]}
                      abbreviation={"POS"}
                      path={projectPath}
                      key={"POS" + "INFO"}
                    ></InfoButton>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className={styles.content}>
            {data ? (
              uiLoader()
            ) : (
              <div className={styles.gridContainer}>
                <div className={styles.item1 && styles.gridItem}>
                  <PatternlibSpinner spin></PatternlibSpinner>
                </div>
                <div className={styles.item2 && styles.gridItem}>
                  {t("Spinner.loadProduct")}
                </div>
              </div>
            )}
            <div className={styles.completionWrapper}>
              <div className={styles.inputWrapper}>
                <AddComparisonButton
                  part={partkey}
                  path={path}
                  data={data}
                  onSave={createToast()}
                ></AddComparisonButton>
                <InfoButton
                  title={t("infotexts.general.comparison.title")}
                  abbreviation={"comparison"}
                  path={"general"}
                ></InfoButton>
              </div>
              <PatternlibButton
                label={t("configurator.next")}
                size="small"
                onClick={toSummary}
              ></PatternlibButton>
            </div>
          </div>
        </div>
      </div>
      <PatternlibToast
        position="top-right"
        ref={toastRef}
        width={screen === "isMobile" ? "256px" : "408px"}
      ></PatternlibToast>
      <PatternlibOverlayContainer
        target="center"
        show={loading}
        disable-click-handlers
        ref={overlayRef}
      >
        <div slot="content" className={styles.modalContent}>
          <PatternlibSpinner spin></PatternlibSpinner>
        </div>
      </PatternlibOverlayContainer>
    </>
  );
}

export default Configurator;
